/**
 * Top-level app state and navigation.
 * Manages screen routing and initial theme loading.
 */

import { loadTheme } from "./theme";
import { renderHome, type ScreenId } from "./screens/home";
import { renderTeambuilder } from "./screens/teambuilder";
import { renderTrainer } from "./screens/trainer";
import { renderLobby } from "./screens/lobby";
import { renderBattle } from "./screens/battle";
import { renderSpectate } from "./screens/spectate";
import { renderReplay } from "./screens/replay";
import { renderBotLobby } from "./screens/botlobby";
import { renderBotBattle } from "./screens/botbattle";
import { clearChildren } from "./utils/dom";
import { getBattleSession } from "./battle-session";

/** Initialize the app: load theme, render initial screen. */
export function initApp(): void {
  loadTheme();

  const appRoot = document.getElementById("app");
  if (!appRoot) throw new Error("App root element not found");

  const navigate = (screen: ScreenId): void => {
    switch (screen) {
      case "home":
        renderHome(appRoot, navigate);
        break;
      case "teambuilder":
        renderTeambuilder(appRoot, navigate);
        break;
      case "trainer":
        renderTrainer(appRoot, navigate);
        break;
      case "lobby-host": {
        const prev = appRoot.firstChild;
        const cleanup = prev && (prev as HTMLElement & { __cleanupLobby?: () => void }).__cleanupLobby;
        cleanup?.();
        renderLobby(appRoot, navigate, true);
        break;
      }
      case "replay":
        renderReplay(appRoot, navigate);
        break;
      case "battle": {
        const session = getBattleSession();
        if (!session) {
          toastFromApp("No active battle session");
          navigate("home");
          return;
        }
        renderBattle(appRoot, navigate, session);
        break;
      }
      case "lobby-join": {
        const prev = appRoot.firstChild;
        const cleanup = prev && (prev as HTMLElement & { __cleanupLobby?: () => void }).__cleanupLobby;
        cleanup?.();
        renderLobby(appRoot, navigate, false);
        break;
      }
      case "spectate":
        renderSpectate(appRoot, navigate);
        break;
      case "bot-lobby":
        renderBotLobby(appRoot, navigate);
        break;
      case "bot-battle":
        renderBotBattle(appRoot, navigate);
        break;
      default:
        renderPlaceholder(appRoot, screen, navigate);
    }
  };

  navigate("home");
}

function toastFromApp(message: string): void {
  // Avoid importing toast component to keep app.ts lightweight
  const div = document.createElement("div");
  div.textContent = message;
  div.className = "toast";
  document.body.appendChild(div);
  setTimeout(() => div.remove(), 3000);
}

function renderPlaceholder(
  container: HTMLElement,
  screen: string,
  navigate: (screen: ScreenId) => void,
): void {
  clearChildren(container);
  const main = document.createElement("main");
  main.className = "app-main";
  const heading = document.createElement("h2");
  heading.textContent = screen.charAt(0).toUpperCase() + screen.slice(1);
  const para = document.createElement("p");
  para.textContent = "This screen is under construction. Coming soon!";
  main.appendChild(heading);
  main.appendChild(para);

  const backBtn = document.createElement("button");
  backBtn.textContent = "Back to Home";
  backBtn.className = "nav-button";
  backBtn.addEventListener("click", () => {
    navigate("home");
  });
  main.appendChild(backBtn);

  container.appendChild(main);
}
